import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import { 
  LayoutDashboard, 
  Users, 
  Target, 
  Calendar, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  Mail,
  UserCog,
  BarChart3,
  Bell,
  Lightbulb,
  Search,
  TrendingUp,
  Palette,
  Megaphone,
  PhoneCall,
  CircleDollarSign,
  Plug,
  Rocket,
  CreditCard,
  Wallet
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';

const seoNavItems = [
  { path: '/setup', label: 'Configuration initiale', icon: Rocket, adminOnly: true },
  { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/clients', label: 'Clients', icon: Users },
  { path: '/keywords', label: 'Mots-clés', icon: Target },
  { path: '/leads', label: 'Leads', icon: Mail },
  { path: '/calendar', label: 'Calendrier', icon: Calendar },
  { path: '/reports', label: 'Rapports', icon: FileText },
  { path: '/google-analytics', label: 'Google Analytics', icon: BarChart3 },
  { path: '/search-console', label: 'Search Console', icon: Search },
  { path: '/keyword-evolution', label: 'Évolution mots-clés', icon: TrendingUp },
  { path: '/lead-analytics', label: 'Analyse des leads', icon: Mail },
  { path: '/conversions', label: 'Conversions & ventes', icon: CircleDollarSign },
  { path: '/consultants', label: 'Consultants', icon: UserCog, adminOnly: true },
  { path: '/user-management', label: 'Utilisateurs', icon: Users, adminOnly: true },
  { path: '/email-branding', label: 'Branding emails', icon: Palette, adminOnly: true },
  { path: '/opportunities', label: 'Opportunités', icon: Lightbulb, adminOnly: true },
  { path: '/integrations', label: 'Intégrations & Connecteurs', icon: Plug, adminOnly: true },
  { path: '/billing', label: 'Abonnement', icon: CreditCard, adminOnly: true },
  { path: '/finance', label: 'Résultats', icon: Wallet },
  { path: '/notifications', label: 'Notifications', icon: Bell },
];

const seaNavItems = [
  { path: '/setup', label: 'Configuration initiale', icon: Rocket, adminOnly: true },
  { path: '/sea/dashboard', label: 'Tableau de bord SEA', icon: LayoutDashboard },
  { path: '/sea/campaigns', label: 'Campagnes Ads', icon: Megaphone },
  { path: '/clients', label: 'Clients', icon: Users },
  { path: '/leads', label: 'Leads (formulaires)', icon: Mail },
  { path: '/sea/lead-analytics', label: 'Analyse leads SEA', icon: PhoneCall },
  { path: '/conversions', label: 'Conversions & ventes', icon: CircleDollarSign },
  { path: '/sea/reports', label: 'Rapports SEA', icon: FileText },
  { path: '/calendar', label: 'Calendrier', icon: Calendar },
  { path: '/consultants', label: 'Consultants', icon: UserCog, adminOnly: true },
  { path: '/user-management', label: 'Utilisateurs', icon: Users, adminOnly: true },
  { path: '/email-branding', label: 'Branding emails', icon: Palette, adminOnly: true },
  { path: '/integrations', label: 'Intégrations & Connecteurs', icon: Plug, adminOnly: true },
  { path: '/billing', label: 'Abonnement', icon: CreditCard, adminOnly: true },
  { path: '/finance', label: 'Résultats', icon: Wallet },
  { path: '/notifications', label: 'Notifications', icon: Bell },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { mode, setMode } = useMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const filteredNavItems = useMemo(() => {
    const items = mode === 'sea' ? seaNavItems : seoNavItems;
    return items.filter(item => !item.adminOnly || user?.role === 'admin');
  }, [user?.role, mode]);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
        setUnreadCount(data.count || 0);
      } catch (e) {
        // Silently handle unread count errors (non-critical)
      }
    };
    if (user) {
      fetchUnread();
      const interval = setInterval(fetchUnread, 30000);
      return () => clearInterval(interval);
    }
  }, [user, API]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="app-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-neutral-200 transform transition-transform duration-200 ease-out lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-neutral-200">
            <Link to={mode === 'sea' ? '/sea/dashboard' : '/dashboard'} className="flex items-center gap-2" data-testid="logo-link">
              <div className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${
                mode === 'sea' ? 'bg-orange-600' : 'bg-[#002FA7]'
              }`}>
                <span className="text-white font-heading font-bold text-[10px] tracking-tight">
                  {mode === 'sea' ? 'SEA' : 'SEO'}
                </span>
              </div>
              <span className="font-heading font-bold text-lg text-neutral-900">Aegis</span>
            </Link>
            <button 
              className="lg:hidden p-1 hover:bg-neutral-100 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mode toggle SEO / SEA */}
          <div className="px-4 py-3 border-b border-neutral-200">
            <div className="bg-neutral-100 rounded-lg p-1 flex" data-testid="mode-toggle">
              <button
                type="button"
                onClick={() => { setMode('seo'); navigate('/dashboard'); }}
                className={`flex-1 text-sm font-semibold py-1.5 rounded-md transition-all ${
                  mode === 'seo'
                    ? 'bg-white text-[#002FA7] shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
                data-testid="mode-seo"
              >
                SEO
              </button>
              <button
                type="button"
                onClick={() => { setMode('sea'); navigate('/sea/dashboard'); }}
                className={`flex-1 text-sm font-semibold py-1.5 rounded-md transition-all ${
                  mode === 'sea'
                    ? 'bg-white text-orange-600 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
                data-testid="mode-sea"
              >
                SEA
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4" data-testid="main-nav">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              const showBadge = item.path === '/notifications' && unreadCount > 0;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.path.slice(1)}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-body flex-1">{item.label}</span>
                  {showBadge && (
                    <span
                      className="ml-auto min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold px-1.5"
                      data-testid="unread-notifications-badge"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-neutral-200">
            <div className="flex items-center gap-3 p-2">
              <Avatar className="w-9 h-9 border border-neutral-200">
                <AvatarFallback className="bg-neutral-100 text-neutral-700 text-sm font-medium">
                  {user?.name ? getInitials(user.name) : 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">{user?.name}</p>
                <p className="text-xs text-neutral-500 truncate">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-white border-b border-neutral-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-neutral-100 rounded"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-heading font-bold text-xl text-neutral-900 hidden sm:block">
              {filteredNavItems.find(item => location.pathname.startsWith(item.path))?.label || (mode === 'sea' ? 'SEA' : 'Dashboard')}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8 border border-neutral-200">
                    <AvatarFallback className="bg-neutral-100 text-neutral-700 text-xs font-medium">
                      {user?.name ? getInitials(user.name) : 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-neutral-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2" data-testid="settings-link">
                    <Settings className="w-4 h-4" />
                    Paramètres
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 bg-neutral-50">
          {children}
        </main>
      </div>
    </div>
  );
}
